'use strict';

import {
	changeLangToCurrent,
	getCurrentLang
} from '../../components/langSwitcher/langSwitcher';
import {
	capitalizeFirstLetter,
	clamp,
	findSelectionAndCrmData,
	flatClick, getFieldsFromAPI,
	getMoneyToken,
	getObjectFromLocalStorage,
	getTowerName, groupNumbersByDynamicRange,
	handleFavoriteLogic,
	isNumeric,
	numberWithCommas, parseFormattedNumber,
	roundToNearest,
	setParametersDependsOnCanvasArea, translateAPIIntoDreamsName,
	userPrevent,
} from '../../../functions';
import {
	AVAILABLE_STATUS,
	Buildings,
	cardsData, components, isDesktop,
	isMobile, isNoMenu, isWeb,
	projectMode,
	settingsData,
	threeJsVariables, TRoot, UIRoot,
} from '../../../settings';
import {CardTypes, FilterTypes, ViewMode} from "../../../common";
import gsap from "gsap";
import {facingDictionary} from "../../cards/cardsConfig";
import {hideTooltip} from "../../components/flatTooltip/flatTooltip";
import {hoverOutPreviousSelection, selectionBoxesSetStatusColor} from '../../threejs/raycast';
import {
	createMultipleSearch,
	updateMultiSearch,
	toggleMultiSearchButton,
	updateMultiSearchState
} from './multipleSearch';
import {UnitCardClass} from "../../cards/unitCards/UnitCardClass";
import {addBmbyEditButton, setActiveClassHandler } from '../../cards/unitCards/unitCardFunctions';
import {Switch} from "../../interfaces/switchers/Switch";
import {SearchResultsNotificator} from "./SearchResultsNotificator";
import {Favorite} from '../../components/Favorite/Favorite';
import {BaseElement} from "../../interfaces/BaseElement";
import {Price} from '../../cards/Price';
import {showOrHideCylinderFloorNumbers} from "../../components/cylinderFloorNumbers/cylinderFloorNumbers";
import {Vantages} from "../../components/vantage/Vantage";
import { Cookie } from '../../Cookie/Cookie';
import { MultipleSelector } from '../../interfaces/MultipleSelector';
import { VideoPopUp } from '../../components/VideoPopUp';
import { IntersectionLegenda } from '../../components/IntersectionLegenda';
import { Color, LinearSRGBColorSpace } from 'three';
import {BmbyMode} from "../../mode/BmbyMode";
import {ShrinkSearch} from "./ShrinkSearch/ShrinkSearch";

export const selectedBuildings = new Set();
const _color = new Color();
const _collapsedGroups = [];

function createSearchModule() {
	let containerMain = UIRoot.querySelector('.search__container-main');
	if (containerMain)
		return new BaseElement(containerMain);

	containerMain = new BaseElement()
		.addClass('search__container-main')
		.appendTo(UIRoot);
	if (VideoPopUp.isActive()) {
		VideoPopUp.addSearchResizeCallback(containerMain.DOMElement);
	}
	if (isNoMenu)
		containerMain.addClass('no-menu-mode');
	const containerTop = new BaseElement()
		.addClass('search__container-top')
		.appendTo(containerMain.DOMElement);
	const containerBottom = new BaseElement()
		.addClass('search__container-bottom')
		.appendTo(containerMain.DOMElement);
	const containerTopWrapper = new BaseElement()
		.addClass('search__top-wrapper')
		.appendTo(containerTop.DOMElement);

		containerMain
			.event('transitionend', () => {
				if (containerMain.DOMElement?.getAttribute('state') !== 'opened')
					containerMain.remove();
			});

	const searchResultsWrapper = new BaseElement()
		.addClass('search__result-wrapper')
		.appendTo(containerBottom.DOMElement);
	const isPrice = new Price().showPrice ? 'show-price' : 'no-price';
	const searchResultsSort = new BaseElement()
		.addClass('search__sort', isPrice)
		.appendTo(containerBottom.DOMElement);
	const searchList = new BaseElement('ul')
		.addClass('search__result-list')
		.event('click', searchListClickHandler)
		.appendTo(containerBottom.DOMElement);

		searchList
			.event('click', handleCardSelect);

	createSearchResultsCount(searchResultsWrapper.DOMElement);
	createSearchResultsSortMenu(searchResultsSort.DOMElement);
	createSearchOptionsElements(containerTopWrapper.DOMElement);
	createMultipleSearch();

	if (containerTopWrapper.DOMElement.children.length > 3)
		new ShrinkSearch(containerTopWrapper.DOMElement);

	return containerMain;
}

// function hideFiltersMoreThan (amount) {
// 	const topContainer = document.querySelector('.search__top-wrapper');
// 	const allFilters = [...topContainer.getElementsByClassName('search__option')];
// 	allFilters.slice(amount).forEach(filter => {
// 		filter.classList.add('hidden');
// 	});
// }

function searchListClickHandler (event) {
	if (!event.target.closest('.search__group-title'))
		return;

	const groupWrapper = event.target.closest('.search__building-group');
	const buildingName = groupWrapper.getAttribute('building-name');
	const groupList = groupWrapper.querySelector('.search__group-list');
	const groupState = groupWrapper.getAttribute('state');
	if (groupState === 'collapsed') {
		_collapsedGroups.splice(_collapsedGroups.indexOf(buildingName), 1);
		groupWrapper.setAttribute('state', 'expanded');
		groupList.style.removeProperty('display');
	} else if (groupState === 'expanded') {
		_collapsedGroups.push(buildingName);
		groupWrapper.setAttribute('state', 'collapsed');
		groupList.addEventListener('transitionend', () => { groupList.style.display = 'none'; }, {once: true});
	}
}

function createSearchResultsCount (parent) {
	const isHideResultsCount = config.features.search?.hideResultsCount;
	new BaseElement()
		.addClass('search__result-count', isMobile || isHideResultsCount && 'hidden')
		.appendTo(parent)
		.DOMElement.insertAdjacentHTML('beforeend', `
        <span class="search__result-count--amount">0</span>
        <p class="language-string search__result-count--number" data-dictionary="${'apartments found'}">
            ${changeLangToCurrent('apartments found')}
        </p>
    `);
}

function createMinMaxInputs (wrapper, minValue, maxValue, field) {
	const labelMin = new BaseElement('label')
		.addClass('search__label', 'search__label-min')
		.appendTo(wrapper);
	const labelMax = new BaseElement('label')
		.addClass('search__label', 'search__label-max')
		.appendTo(wrapper);
	if (field.key === 'salePrice' || field.key === 'rentPrice') {
		const moneyToken = getMoneyToken();
		labelMax.appendElement(moneyToken.DOMElement.cloneNode(true));
		labelMin.appendElement(moneyToken.DOMElement.cloneNode(true));
	}

	const elementInputMinValue = new BaseElement('input')
		.addClass('search__option-input', 'search__option-input--min', 'text', 'callout')
		.addAttribute('type', 'text')
		.addAttribute('value', `${numberWithCommas(minValue, true)}`)
		.addAttribute('data-type', `${field.key}`)
		.addAttribute('data-value', `${minValue}`)
		.addAttribute('inputmode', 'numeric')
		.addAttribute('pattern', '[0-9]*')
		.event('focus', handleRangeInputs)
		.event('focusout', handleRangeInputs)
		.event('change', handleRangeInputs)
		.event('keyup', handleRangeInputs)
		.appendTo(labelMin.DOMElement)
		.DOMElement;

	const elementInputMaxValue = new BaseElement('input')
		.addClass('search__option-input', 'search__option-input--max', 'text', 'callout')
		.addAttribute('type', 'text')
		.addAttribute('value', `${numberWithCommas(maxValue, true)}`)
		.addAttribute('data-type', `${field.key}`)
		.addAttribute('data-value', `${maxValue}`)
		.addAttribute('inputmode', 'numeric')
		.addAttribute('pattern', '[0-9]*')
		.event('focus', handleRangeInputs)
		.event('focusout', handleRangeInputs)
		.event('change', handleRangeInputs)
		.event('keyup', handleRangeInputs)
		.appendTo(labelMax.DOMElement)
		.DOMElement;

	return {elementInputMinValue, elementInputMaxValue};
}

export function createRange (inputCondition, elementContainer, field) {
	const minValue = Math.floor(Math.min(...inputCondition));
	const maxValue = Math.ceil(Math.max(...inputCondition));
	const step = maxValue > 100 ? (maxValue - minValue) / 100 : 1;
	const minGap = 0;

	const wrapper = new BaseElement()
		.addClass('search__wrapper', 'search__wrapper-range')
		.appendTo(elementContainer);

	const {searchState} = settingsData;
	const rangeDataInState = searchState.find(state => state.type === field.key);
	const startMinValue = (rangeDataInState) ? rangeDataInState.min : minValue;
	const startMaxValue = (rangeDataInState) ? rangeDataInState.max : maxValue;

	const {elementInputMinValue, elementInputMaxValue} =
		createMinMaxInputs(wrapper.DOMElement, startMinValue, startMaxValue, field);

	const rangeWrapper = new BaseElement()
		.addClass('search__range-wrapper')
		.appendTo(wrapper.DOMElement);
	const rangeContainer = new BaseElement()
		.addClass('search__range-container')
		.addAttribute('data-type', field.key)
		.appendTo(rangeWrapper.DOMElement);
	const rangeSliderTrack = new BaseElement()
		.addClass('search__range-slider-track')
		.appendTo(rangeContainer.DOMElement);
	const rangeOne = new BaseElement('input')
		.addClass('search__range-input', 'search__range-input--one', `search__range-input--${field.key}`)
		.appendTo(rangeContainer.DOMElement);
	const rangeTwo = new BaseElement('input')
		.addClass('search__range-input', 'search__range-input--two', `search__range-input--${field.key}`)
		.appendTo(rangeContainer.DOMElement);

	const rangeValues = {
		'min': minValue,
		'max': maxValue,
		'value': {
			'startMinValue': startMinValue,
			'startMaxValue': startMaxValue,
		},
		'startMinValue': startMinValue,
		'startMaxValue': startMaxValue,
		'step': step,
		'data-type': field.key,
		'type': 'range',
	};

	setDataRangeInputs(rangeOne.DOMElement, rangeValues);
	setDataRangeInputs(rangeTwo.DOMElement, rangeValues);
	fillTrackColor(rangeSliderTrack.DOMElement, rangeOne.DOMElement, rangeTwo.DOMElement);

	if (parseInt(rangeOne.DOMElement.value) === maxValue) {
		rangeOne.DOMElement.style.zIndex = 2;
		rangeTwo.DOMElement.style.zIndex = 1;
	} else if (parseInt(rangeTwo.DOMElement.value) === maxValue) {
		rangeOne.DOMElement.style.zIndex = 1;
		rangeTwo.DOMElement.style.zIndex = 2;
	}

	rangeOne.event('input', (e) => {
		e.preventDefault();
		if (parseInt(rangeTwo.DOMElement.value) - parseInt(rangeOne.DOMElement.value) <= minGap)
			rangeOne.DOMElement.value = parseInt(rangeTwo.DOMElement.value) - minGap;

		elementInputMinValue.value = numberWithCommas(Math.round(rangeOne.DOMElement.value), true);
		fillTrackColor(rangeSliderTrack.DOMElement, rangeOne.DOMElement, rangeTwo.DOMElement);

		const updateStateElement = searchState.find(item => item.type === field.key);
		if (!updateStateElement.hasOwnProperty('checked'))
			updateStateElement.min = rangeOne.DOMElement.value;

		if (!components.mobileSearch) {
			searchGetResults();
			updateMultiSearch();
		} else {
			components.mobileSearch.predict();
		}


		const currentMax = parseInt(updateStateElement.max, 10);
		const currentMin = parseInt(updateStateElement.min, 10);
		const startMax = parseInt(updateStateElement.startMax, 10);
		const startMin = parseInt(updateStateElement.startMin, 10);
		if (currentMax === startMax && currentMin === startMin) {
			rangeOne.DOMElement.closest('.search__option').setAttribute('state', 'inactive');
		} else {
			rangeOne.DOMElement.closest('.search__option').setAttribute('state', 'active');
		}

		// if (isMobile) {
		// 	FiltersCounter.update();
		// }
	});

	rangeTwo.event('input', (e) => {
		e.preventDefault();

		if (parseInt(rangeTwo.DOMElement.value) - parseInt(rangeOne.DOMElement.value) <= minGap)
			rangeTwo.DOMElement.value = parseInt(rangeOne.DOMElement.value) + minGap;

		elementInputMaxValue.value = numberWithCommas(Math.round(rangeTwo.DOMElement.value), true);
		fillTrackColor(rangeSliderTrack.DOMElement, rangeOne.DOMElement, rangeTwo.DOMElement);

		const updateStateElement = searchState.find(item => item.type === field.key);
		if (!updateStateElement.hasOwnProperty('checked'))
			updateStateElement.max = rangeTwo.DOMElement.value;

		if (!components.mobileSearch) {
			searchGetResults();
			updateMultiSearch();
		} else {
			components.mobileSearch.predict();
		}

		
		const currentMax = parseInt(updateStateElement.max, 10);
		const currentMin = parseInt(updateStateElement.min, 10);
		const startMax = parseInt(updateStateElement.startMax, 10);
		const startMin = parseInt(updateStateElement.startMin, 10);
		if (currentMax === startMax && currentMin === startMin) {
			rangeTwo.DOMElement.closest('.search__option').setAttribute('state', 'inactive');
		} else {
			rangeTwo.DOMElement.closest('.search__option').setAttribute('state', 'active');
		}
		

		// FiltersCounter.update();
	});

	if (!settingsData.searchState.find(item => item.type === field.key)) {
		settingsData.searchState.push({
			type: field.key,
			startMin: minValue,
			startMax: maxValue,
			min: minValue,
			max: maxValue,
		});
	}
}

function setDataRangeInputs (range, rangeValues) {
	for (const key in rangeValues) {
		if (key === 'value') {
			const isFirst = range.classList.contains('search__range-input--one');
			range.setAttribute(key, isFirst ? rangeValues[key].startMinValue : rangeValues[key].startMaxValue);
			continue;
		}
		range.setAttribute(key, rangeValues[key]);
	}
}

function fillTrackColor (rangeSliderTrack, rangeOne, rangeTwo) {
	const minValue = rangeOne.min;
	const maxValue = rangeOne.max;
	const percent1 = ((rangeOne.value - minValue) / (maxValue - minValue)) * 100;
	const percent2 = ((rangeTwo.value - minValue) / (maxValue - minValue)) * 100;

	const color1 = 'rgba(120, 120, 128, 0.2)';
	const color2 = 'var(--brand2)';

	const rangeSliderTrackCorrection = 2;
	rangeSliderTrack.style.background = `linear-gradient(to right, ${color1} ${percent1 - rangeSliderTrackCorrection}%, ${color2} ${percent1 - rangeSliderTrackCorrection}%, ${color2} ${percent2 + rangeSliderTrackCorrection}%, ${color1} ${percent2 + rangeSliderTrackCorrection}%)`;
}

export function createCheckbox(inputCondition, elementContainer, field) {
    const {searchState} = settingsData;
    const rangeDataInState = searchState.find(state => state.type === field.key);
    const wrapper = new BaseElement()
        .addClass('search__wrapper', 'search__wrapper-checkbox')
        .addAttribute('type', `${field.key}`)
        .appendTo(elementContainer);

    let searchSwitcher = null;
    let isRange = null;

    const isSwitcher =
        field.key === 'accessible' ||
        field.key === 'treatmentRoom' ||
		field.key === 'tenantPrice' ||
        field.key === 'balconySize' && field.visibility === 'check-mark' ||
        field.key === 'roofBalconySize' && field.visibility === 'check-mark' ||
        field.key === 'coveredBalconySize' && field.visibility === 'check-mark' ||
        field.key === 'basementSize' && field.visibility === 'check-mark' ||
        field.key === 'gardenSize' && field.visibility === 'check-mark';
    if (isSwitcher) {
        searchSwitcher = new Switch(wrapper.DOMElement);
        wrapper.addAttribute('selection-type', 'switch');
    } else if (isNumeric(inputCondition[0]) && inputCondition.length > 6 && inputCondition.length < 10) {
        // const clamped = inputCondition.filter(num => num < 6);
		inputCondition.length = 6;
		inputCondition[5] = inputCondition[5] + '+';
        // inputCondition = clamped;
    } else if (isNumeric(inputCondition[0]) && inputCondition.length >= 10) {
        inputCondition = groupNumbersByDynamicRange(inputCondition);
        isRange = true;
    }

    inputCondition.forEach((value, index) => {
        let elementInputCheckbox;

		if (isRange && isEmptyRange(value, field.key))
			return;

        if (isSwitcher) {
            searchSwitcher.checkbox.setAttribute('id', `${field.key}_${index}`);
            searchSwitcher.checkbox.setAttribute(`data-${field.key}`, `${value}`);
            searchSwitcher.checkbox.setAttribute(`data-type`, `${field.key}`);
        } else {
            elementInputCheckbox = new BaseElement('input')
                .addClass('search__option-input', 'search__option-checkbox')
                .setAttributes({
                    'type': 'checkbox',
                    'id': `${field.key}_${index}`,
                    [`data-${field.key}`]: `${value}`,
                    [`data-type`]: `${field.key}`,
                })
                .appendTo(wrapper.DOMElement);

			if (isRange)
				elementInputCheckbox.addAttribute('data-is-range', true)
        }

        if (rangeDataInState) {
			let isChecked;
			if (isRange) {
				isChecked = value.some(item => rangeDataInState.checked.value.includes(item));
			} else {
				isChecked = rangeDataInState.checked.value.indexOf(value);
			}

            if (isSwitcher) {
                searchSwitcher.checked = Boolean(rangeDataInState.checked.value[0]);
            } else if (isChecked !== -1) {
                elementInputCheckbox.addAttribute(`checked`, true);
            }
        }

        let elementLabelCheckbox = null;
        if (isSwitcher) {
            searchSwitcher.slider.setAttribute('data', `${field.key}_${index}`);
        } else {
            elementLabelCheckbox = new BaseElement('label')
                .addClass('search__option-label', 'text')
                .addAttribute('for', `${field.key}_${index}`)
                .appendTo(wrapper.DOMElement);
        }

        if (field.hasOwnProperty('exceptions')) {
            value = field.exceptions[value];

            elementLabelCheckbox?.addClass('language-string', 'search__option__room__text');
            elementLabelCheckbox?.addAttribute('data-dictionary', `${value}`);
            elementLabelCheckbox?.DOMElement
                .insertAdjacentHTML('afterbegin', `${changeLangToCurrent(value)}`);
        } else if ((typeof value !== 'number' && !isRange || (value === 0 && field.key !== 'floorNum')) && elementLabelCheckbox?.DOMElement) {
            if (value === 0 && field.key === 'roomNum')
                value = 'Studio';
            elementLabelCheckbox?.addClass('language-string', 'search__option__room__text');
            elementLabelCheckbox?.addAttribute('data-dictionary', `${value}`);
            elementLabelCheckbox?.DOMElement
                .insertAdjacentHTML('afterbegin', `${changeLangToCurrent(value)}`);

            if (isMobile) {
                switch (value) {
                    case 'Apartment':
                        elementLabelCheckbox?.addClass('icon-building1');
                        break;
                    case 'Penthouse':
                    case 'Mini Penthouse':
                        elementLabelCheckbox?.addClass('icon-building3');
                        break;
                    case 'Duplex':
                        elementLabelCheckbox?.addClass('icon-building4');
                        break;
                    case 'Garden Apartment':
                        elementLabelCheckbox?.addClass('icon-building2');
                        break;

                }
            }
        } else if (field.key === 'floorNum' && (settingsData.groundFloor.isActive && (parseInt(settingsData.groundFloor.floor) === parseInt(value)))) {
            const shortLabel = settingsData.groundFloor.shortLabel;
            elementLabelCheckbox?.addClass('language-string', 'search__option__room__text');
            elementLabelCheckbox?.addAttribute('data-dictionary', `${shortLabel}`);
            elementLabelCheckbox?.DOMElement
                .insertAdjacentHTML('afterbegin', `${changeLangToCurrent(shortLabel)}`);
        } else if (field.key === 'salePrice' && !isRange) {
			elementLabelCheckbox?.innerHtml(numberWithCommas(parseInt(value)))
		} else {
            if (elementLabelCheckbox?.DOMElement)
                elementLabelCheckbox?.DOMElement
                    .insertAdjacentHTML('afterbegin', `${value}`);
        }
        searchSwitcher
            ? searchSwitcher.listener = checkBoxHandler
            : elementInputCheckbox?.event('change', checkBoxHandler);
    });

    const startValue = rangeDataInState ? rangeDataInState.checked.value : [];
    if (!settingsData.searchState.find(item => item.type === field.key)) {
        settingsData.searchState.push({
            type: field.key,
            isRange,
            checked: {
                state: [...inputCondition],
                value: startValue,
            },
        });
    }
}

function isEmptyRange(range, field) {
	const isValueWithPlus = range.indexOf('+') !== -1;
	const minValue = isValueWithPlus
		? parseFormattedNumber(range.slice(0, -1))
		: parseFormattedNumber(range.split('-')[0].trim());
	const maxValue = !isValueWithPlus && parseFormattedNumber(range.split('-')[1].trim());

	return !settingsData.allCrmData.some(apt => isValueWithPlus ? minValue <= apt[field] : minValue <= apt[field] && apt[field] <= maxValue);
}

export function createDropdowns (inputCondition, elementContainer, field) {
	const minValue = Math.floor(Math.min(...inputCondition));
	const maxValue = Math.ceil(Math.max(...inputCondition));

	const wrapper = new BaseElement()
		.addClass('search__wrapper', 'search__wrapper-dropdown')
		.addAttribute('data-field-key', field.key)
		.appendTo(elementContainer);
	createSelectElementWithGivenData(inputCondition, 'min',field.key)
		.appendTo(wrapper.DOMElement);
	new BaseElement('span')
		.innerHtml('-')
		.appendTo(wrapper.DOMElement);
	createSelectElementWithGivenData(inputCondition, 'max',field.key)
		.appendTo(wrapper.DOMElement);

	if (!settingsData.searchState.find(item => item.type === field.key)) {
		settingsData.searchState.push({
			type: field.key,
			startMin: minValue,
			startMax: maxValue,
			min: minValue,
			max: maxValue,
		});
	}
}

function createSelectElementWithGivenData (data, type, field) {
	const selectElement = new BaseElement('select')
		.addAttribute('select-type', type === 'max' ? 'max' : 'min')
		.addAttribute('data-type',field)
		.event('change', selectFilterChangeHandler)
		.addClass('mobile-select-dropdown');

	let minValue = Math.floor(Math.min(...data));
	const maxValue = Math.ceil(Math.max(...data));
	let step = 1;
	let isPrice = false;

	switch (field) {
		case 'totalSpace':
			step = 10;
			break;
		case 'roomNum':

			break;
		case 'salePrice':
			isPrice = true;
			step = config.features.search.searchConfig?.dropdownValueStep
				? parseInt(config.features.search.searchConfig.dropdownValueStep, 10)
				: 100000;

			new BaseElement('option')
				.addClass('translate-number', 'price')
				.addAttribute('value', minValue)
				.innerHtml(numberWithCommas(minValue, isPrice))
				.appendTo(selectElement.DOMElement);

			minValue = roundToNearest(minValue, step);
			break;


	}

	for (let i = minValue; i < maxValue; i += step) {
		new BaseElement('option')
			.addClass('translate-number', 'price')
			.addAttribute('value', i)
			.innerHtml(numberWithCommas(i, isPrice))
			.appendTo(selectElement.DOMElement);
	}

	new BaseElement('option')
		.addClass('translate-number', 'price')
		.addAttribute('value', maxValue)
		.innerHtml(numberWithCommas(maxValue, isPrice))
		.appendTo(selectElement.DOMElement);

	const selectsChildren = selectElement.DOMElement.children;
	if (type === 'max') {
		selectsChildren[selectsChildren.length - 1].setAttribute('selected', 'selected');
	} else if (type === 'min') {
		selectsChildren[0].setAttribute('selected', 'selected');
	}

	return selectElement;
}

function selectFilterChangeHandler (event) {
	const wrapperDropdown = event.target.closest('.search__wrapper-dropdown');
	const tiedSelectElements = [...wrapperDropdown.querySelectorAll('select')];
	const minSelectElement = tiedSelectElements[0];
	const minValue = parseInt(tiedSelectElements[0].value, 10);
	const maxSelectElement = tiedSelectElements[1];
	const maxValue = parseInt(tiedSelectElements[1].value, 10);
	const fieldKey = wrapperDropdown.getAttribute('data-field-key');
	const {searchState} = settingsData;
	const updateStateElement = searchState.find(item => item.type === fieldKey);
	const targetType = event.target.getAttribute('select-type');

	if (targetType === 'min') {
		if (minValue > maxValue)
			minSelectElement.value = maxSelectElement.value;
		if (!updateStateElement.hasOwnProperty('checked'))
			updateStateElement.min = minSelectElement.value;
	}
	if (targetType === 'max') {
		if (minValue > maxValue)
			maxSelectElement.value = minSelectElement.value;
		if (!updateStateElement.hasOwnProperty('checked'))
			updateStateElement.max = maxSelectElement.value;
	}

	if (parseInt(updateStateElement.startMax, 10) !== parseInt(updateStateElement.max, 10) ||
		parseInt(updateStateElement.startMin, 10) !== parseInt(updateStateElement.min, 10)) {
		event.target.closest('.search__option').setAttribute('state', 'active');
	} else if (parseInt(updateStateElement.startMax, 10) === parseInt(updateStateElement.max, 10) &&
		parseInt(updateStateElement.startMin, 10) === parseInt(updateStateElement.min, 10)) {
		event.target.closest('.search__option').setAttribute('state', 'inactive');
	}

	// FiltersCounter.update();
	if (!components.mobileSearch) {
		searchGetResults();
	} else {
		components.mobileSearch.predict();
	}
}

export function updateSearchResults () {
	searchGetResults();
}

function createSearchOptionsElements (containerTopWrapper) {
	settingsData.searchResult = [];
	// const {fields = config.features.search.searchConfig};
	// const {searchSortState} = getFieldsFromAPI.table();
	const displayPrice = new Price().showPrice;
	let states = getFieldsFromAPI.table().slice();

	if (!displayPrice)
		states = states.filter(el => el.type !== 'salePrice').filter(el => el.type !== 'rentPrice');

	for (const field of getFieldsFromAPI.filter()) {
		const hidePrice = (
			field.key === 'salePrice' ||
			field.key === 'rentPrice' ||
			field.key === 'maintenanceFee' ||
			field.key === 'managementFee'
		) && (!displayPrice);

		let inputCondition = getUniqueUnits(settingsData.allCrmData, field);

		if (hidePrice || inputCondition.length === 1)
			continue;

		if (inputCondition.length === 0 || hidePrice) {
			const item = states.find(state => state.type === field.key);
			const index = states.indexOf(item);

			index !== -1 && states.splice(index, 1);
			continue;
		}

		if (field.key === 'facing')
			inputCondition = inputCondition.reduce((arr, item) => {
				item.forEach(str => {
					const strMod = /^[A-Za-z]*$/.test(str) ? str : facingDictionary()[str].en;

					if (arr.indexOf(strMod) === -1)
						arr.push(strMod);
				});

				return arr;
			}, []);

		const elementContainer = new BaseElement()
			.addClass('search__option')
			.addAttribute('data-type', field.key)
			.appendTo(containerTopWrapper);
		if (isMobile)
			elementContainer.addAttribute('state', 'inactive');
		const elementTitle = new BaseElement('h2')
			.addClass('search__option-title')
			.appendTo(elementContainer.DOMElement);

		/*new BaseElement('span')
		 .addClass(field.icon, 'search__option-title--icon')
		 .appendTo(elementTitle.DOMElement);*/

		// new BaseElement('span')
		// 	.addClass('language-string', 'search__option-title--text', 'text', 'callout')
		// 	.addAttribute('data-dictionary', field.labelKey)
		// 	.innerHtml(changeLangToCurrent(field.labelKey))
		// 	.appendTo(elementTitle.DOMElement);
		createOptionTitleText(elementTitle.DOMElement, field.labelKey);

		if (isMobile && (field.key === 'salePrice' || field.key === 'rentPrice')) {
			new BaseElement('span')
				.innerHtml(', &nbsp')
				.appendTo(elementTitle.DOMElement);
			getMoneyToken().appendTo(elementTitle.DOMElement);
		}

		if (field.key === 'modelName') {
			if (inputCondition.length > 5) {
				const select = new BaseElement('select')
					.addAttribute('multiple', 'multiple')
					.addClass('multiple__select', 'multiple-select')
					.appendTo(elementContainer.DOMElement)
					.DOMElement;

				inputCondition.forEach(condition => {
					new BaseElement('option')
						.addAttribute(`data-${field.key}`, `${condition}`)
						.addAttribute(`data-type`, `${field.key}`)
						.addAttribute('value',condition)
						.innerHtml(condition)
						.appendTo(select);
				});

				if (!settingsData.searchState.find(item => item.type === field.key)) {
					settingsData.searchState.push({
						type: field.key,
						checked: {
							state: [...inputCondition],
							value: [],
						},
					});
				}

				const multipleSelector = new MultipleSelector(select, 'modelName', {
					showClear: true,
					selectAll: false,
					animate: 'fade',
					minimumCountSelected: 100000,
				});

				multipleSelector.minSelected = isMobile ? 2 : 3;

				multipleSelector.addCallback('onClick', (scope, view) => {
					const state = settingsData.searchState.find(state => state.type === field.key);
					if (view.selected) {
						state.checked.value.push(view.value);
					} else {
						state.checked.value = state.checked.value.filter(v => v !== view.value);
					}
					searchGetResults();
				});

				multipleSelector.addCallback('onUncheckAll', (scope) => {
					const state = settingsData.searchState.find(state => state.type === field.key);
					state.checked.value = [];
					searchGetResults();
				});

				multipleSelector.addCallback('onUpdatePlaceholder', (placeholder, selected) => {
					const selectedItems = selected.size;
					if (selectedItems === 0) {
						placeholder.text(changeLangToCurrent('modelTypePlaceholder'));
					} else {
						const data = [...selected].map(el => el.modelname).slice(clamp(selectedItems - multipleSelector.minSelected,0,selectedItems), selectedItems);
						if (data.length > 1) {
							placeholder.text(data.join(' | ').toString());
						} else {
							placeholder.text(data.join('').toString());
						}
					}
				});
			} else {
				createCheckbox(inputCondition, elementContainer.DOMElement, field);
			}
		} else if (field.visibility === 'check-mark' || !(inputCondition.length > 5 || field?.type === FilterTypes.Range)) {
			createCheckbox(inputCondition, elementContainer.DOMElement, field);
		} else {
			isMobile
				? createDropdowns(inputCondition, elementContainer.DOMElement, field)
				: createRange(inputCondition, elementContainer.DOMElement, field);
		}
	}

	// const lastColumns = isMobile ? 1 : displayPrice ? 1 : 2;
	// const searchColumnsAmount = isMobile ? 4 : states.length - lastColumns;
	// document.documentElement.style.setProperty('--searchGridColumns', `${searchColumnsAmount}`);
	// document.documentElement.style.setProperty('--searchGridColumnsLast', `${lastColumns}`);
}

export function createOptionTitleText(parent, labelKey) {
	new BaseElement('span')
		.addClass('language-string', 'search__option-title--text', 'text', 'callout')
		.addAttribute('data-dictionary', labelKey)
		.innerHtml(changeLangToCurrent(labelKey))
		.appendTo(parent);
}

export function createSearchResultsSortMenu (searchResultsSort/*, searchState, params = {}*/) {
	const currency = config.localization.currencyLabel;
	let value;
	switch (currency) {
		case 'EUR':
			value = '€';
			break;
		case 'USD':
			value = '$';
			break;
		case 'UAH':
			value = '₴';
			break;
		case 'NIS':
			value = '₪';
			break;
	}
	document.documentElement.style.setProperty('--currency', value);

	// if (!params.hasOwnProperty('disableListener'))
		searchResultsSort.addEventListener('click', handleSearchSort, false);

	const displayPrice = new Price().showPrice;
	let amountOfColumns = 0;
	for (const sortType of getFieldsFromAPI.table()) {
		const hidePrice = (
			sortType.type === 'salePrice' ||
			sortType.type === 'rentPrice' ||
			sortType.type === 'price2' ||
			sortType.type === 'maintenanceFee' ||
			sortType.type === 'managementFee'
		) && (!displayPrice);

		if (hidePrice || !isDataPresentForTheField(sortType.key))
			continue;

		amountOfColumns++;

		const classNamesItem = (sortType.isActive)
			? 'active'
			: 'inactive';
		const div = new BaseElement()
			.addClass('search__sort-item', classNamesItem)
			.addAttribute('data-sort-type', `${sortType.type}`)
			.addAttribute('data-sort-state', `${sortType.state}`)
			.appendTo(searchResultsSort);
		const classNames = (sortType.state === 'minmax')
			? 'icon-down'
			: 'icon-up';
		new BaseElement('span')
			.addClass(classNames)
			.appendTo(div.DOMElement);

		if (!isMobile) {
			div.addStyle('order', `${sortType.order}`);
			if (sortType.icon) {
				new BaseElement('span')
					.addClass(`${sortType.icon}`)
					.addAttribute('data-type', 'icon')
					.appendTo(div.DOMElement);
			}
			if (sortType.text === '#') {
				new BaseElement('span')
					.innerHtml(sortType.text)
					.appendTo(div.DOMElement);
			}
		} else if (isMobile && sortType.type === 'favorite') {
			new BaseElement('span')
				.addClass(`${sortType.icon}`)
				.addAttribute('data-type', 'icon')
				.appendTo(div.DOMElement);
		}

		switch (sortType.type) {
			case 'maintenanceFee':
			case 'managementFee':
			case 'salePrice':
			case 'price2':
			case 'rentPrice': {
				const element = new BaseElement('span')
					.addClass('language-string', 'search__sort-currency')
					.addAttribute('data-dictionary', currency)
					.appendTo(div.DOMElement);
				element.DOMElement.insertAdjacentHTML('beforeend', `${changeLangToCurrent(currency)}`);
				break;
			}
			default:
				break;
		}
	}

	const lastColumns = displayPrice ? 1 : 2;
	const searchColumnsAmount = amountOfColumns - lastColumns;
	document.documentElement.style.setProperty('--searchGridColumns', `${searchColumnsAmount}`);
	document.documentElement.style.setProperty('--searchGridColumnsLast', `${lastColumns}`);

	// if (isMobile)
	addWrapperAndTextToSortItems();
}

function isDataPresentForTheField(fieldKey) {
	return Boolean(settingsData.allCrmData.filter(item => {
		const currentFieldValue = item[fieldKey];
		return !(currentFieldValue === "" || currentFieldValue === 0 || (Array.isArray(currentFieldValue) && currentFieldValue.length === 0));
	}).length);
}

export function getUniqueUnits (allCrmData, {key}) {
	return allCrmData.reduce((newArr, data) => {
		
		if (BmbyMode.active) {
			if (data.hasOwnProperty(key) && newArr.indexOf(data[key]) === -1 && ['', undefined, null].indexOf(data[key]) === -1) {
				newArr.push(data[key]);
			}
		} else {
			if (data.hasOwnProperty(key) && newArr.indexOf(data[key]) === -1 && ['', undefined, null].indexOf(data[key]) === -1 && data.status === AVAILABLE_STATUS) {
				if (key === 'salePrice' && data.status === AVAILABLE_STATUS) {
					newArr.push(data[key]);
				} else if (key !== 'salePrice') {
					newArr.push(data[key]);
				}
			}
		}
		
		return newArr.sort((a, b) => a - b);
	}, []);
}

function sortSearchResults (state, baseSearchArr) {
	return baseSearchArr.filter(data => {
		let dataValue = data[state.type];

		if (state.type === 'facing')
			dataValue = applyFacingRules(dataValue);

		if (BmbyMode.active) {
			if (['', undefined, null].indexOf(dataValue) === -1 && (selectedBuildings.has(data.buildingEngineId) || selectedBuildings.size === 0)) {
				if (state.checked)
					return getDataForChecked(data, dataValue, state);
				
				if (state.min <= dataValue && dataValue <= state.max)
					return data;
			}
		} else {
			if (data.status === AVAILABLE_STATUS && ['', undefined, null].indexOf(dataValue) === -1 && (selectedBuildings.has(data.buildingEngineId) || selectedBuildings.size === 0)) {
				if (state.checked) {
					return getDataForChecked(data, dataValue, state);
				}

				if (state.min <= dataValue && dataValue <= state.max) {
					return data;
				}
			}
		}
	});
}

export function applyFacingRules (data) {
	if (isNaN(data)) {
		if (typeof data === 'string')
			data = data.split(',');

		return data.map(item => /^[A-Za-z]*$/.test(item) ? item : facingDictionary()[item].en);
	}

	return data;
}

export function getDataForChecked (data, dataValue, state) {
	if (Array.isArray(dataValue)) {
		if (dataValue.length === 0 && state.checked.value.length === 0)
			return data;

		for (const value of dataValue) {
			if (state.checked.value.length > 0) {
				if (state.checked.value.indexOf(value) !== -1) {
					return data;
				}
			} else {
				if (state.checked?.state.indexOf(value) !== -1) {
					return data;
				}
			}
		}
	} else {
		if (state.checked.value.length > 0) {
			if (state.checked.value.indexOf(dataValue) !== -1)
				return data;
		} else {
			if (state.isRange) {
				if (state.checked.value.length > 0) {
					if (state.checked.value.indexOf(dataValue) !== -1)
						return data;
				} else {
					return data;
				}
			} else {
				// if (state.checked?.state.indexOf(dataValue) !== -1)
				// 	return data;

				// check if dataValue is present in checked states + logic for case if some of checked state has the format like '5+'
				if (state.checked?.state.some(val => {
					val = String(val).trim();
					let match = val.match(/^(-?\d+(\.\d+)?)\+?$/);

					if (match) {
						// Handle numeric values
						let num = parseFloat(match[1]);
						return num === dataValue || (val.endsWith('+') && parseFloat(dataValue) >= num);
					}

					// Handle string values (direct match)
					return val === dataValue;
				})) {
					return data;
				}

			}
		}
	}
}

export function searchGetResults (delay = 0, building, addAndShowResults = true) {
	let {allCrmData, searchResult, searchState} = settingsData;
	const filteredCrmData = filterDuplicatesFromAllCrmData(allCrmData);
	let baseSearchArr = [...filteredCrmData];
	for (const state of searchState) {
		searchResult = sortSearchResults(state, baseSearchArr);
		baseSearchArr = [...searchResult];
	}
	// const {searchSortState} = config.features.search;
	const sortState = getFieldsFromAPI.table().find(state => state.isActive);

	if (!isMobile)
		searchResult = sortArray(sortState.state, sortState.type, searchResult);

	if (addAndShowResults) {
		showSearchResults(searchResult);

		const activeBuilding = building || Buildings.getActiveBuilding();
		const timeOut = (delay !== 0)
			? (delay + 1.5) * 1000
			: delay;
		setTimeout(() => {
			changeSelectionBoxesStatusColor(searchResult, activeBuilding);
			projectMode.Cylinder.cylinderBuildingLabels.updateResults();
		}, timeOut);

		settingsData.searchResult = searchResult;
	}
	SearchResultsNotificator.update();
	projectMode.Cylinder.cylinderBuildingLabels.updateResults();

	return searchResult;
}

export function filterDuplicatesFromAllCrmData (allCrmData) {
	const data = new Set();
	allCrmData.forEach(element => {
		data.add(element);
	});

	return data;
}

export function changeSelectionBoxesStatusColor (searchResult, activeBuilding) {
	let selectionBoxesActiveTower;
	if ((projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty'))) || config.features.privateHouseMode) {
		const buildings = Buildings.getBuildingsArray();
		const apartments = [];
		buildings.forEach(building => apartments.push(...building.getApartments()));
		selectionBoxesActiveTower = apartments;
	} else {
		if (!activeBuilding)
			activeBuilding = Buildings.getActiveBuilding();
		selectionBoxesActiveTower = activeBuilding?.apartments;
	}

	if (!selectionBoxesActiveTower || selectionBoxesActiveTower.length === 0)
		return;

	if (!searchResult)
		searchResult = settingsData.searchResult;

	for (const selBox of selectionBoxesActiveTower) {
		const itemInSearch = searchResult.find(item => item.engineId === selBox.name);

		if (itemInSearch) {
			selBox.userData.isInSearch = true;
			selectionBoxesSetStatusColor(selBox, 'lock');

			if (selBox.userData.isClicked) {
				selectionBoxesSetStatusColor(selBox, 'click');
			}
		} else {
			selBox.userData.isInSearch = false;
			selectionBoxesSetStatusColor(selBox, 'unlock');

			if (selBox.userData.isClicked) {
				selectionBoxesSetStatusColor(selBox, 'click');
			}

			if (settingsData.buildingDestroyed) {
				if (settingsData.currentFloor !== selBox.userData.floor) {
					selectionBoxesSetStatusColor(selBox, 'unlock');
				}
			} else {
				if (!selBox.userData.isClicked) {
					selectionBoxesSetStatusColor(selBox, 'unlock');
				}
			}
		}
	}
}

export function showSearchResults (searchResult) {
	// Search results counter

	searchResult = searchResult.filter(element => {
		return selectedBuildings.has(element.buildingEngineId) || selectedBuildings.size === 0;
	});

	const searchResultsCount = document.querySelector('.search__result-count--amount');
	const searchResultsNumber = document.querySelector('.search__result-count--number');

	let text;
	if (Buildings.getBuildingsInfoLength() < 2) {
		text = searchResult.length > 1
			? changeLangToCurrent('apartments found')
			: changeLangToCurrent('apartment found');
	} else {
		text = changeLangToCurrent('apartments found');
	}

	if (searchResultsNumber && searchResultsNumber.innerText !== text)
		searchResultsNumber.innerHTML = ` ${text}`;
	if (searchResultsCount)
		searchResultsCount.innerText = `${searchResult.length}`;

	const sortPanel = document.querySelector('.search__sort');
	if (searchResult.length === 0) {
		sortPanel ? sortPanel.classList.add('hide') : null;
	} else {
		sortPanel ? sortPanel.classList.remove('hide') : null;
	}

	setResultsInTheHtmlList(searchResult);
}

export function setSearchContainerMaxHeight () {
	const containerMain = UIRoot.querySelector('.search__container-main');
	if (isMobile || !containerMain)
		return;

	const containerMainHeight = containerMain.getBoundingClientRect().height;
	const searchList = containerMain.querySelector('.search__result-list');
	const containerTop = document.querySelector('.search__container-top');
	const containerTopHeight = containerTop.getBoundingClientRect().height;
	const resultWrapper = document.querySelector('.search__result-wrapper');
	const resultWrapperHeight = resultWrapper.getBoundingClientRect().height;
	const sortMenu = document.querySelector('.search__sort');
	const sortMenuHeight = sortMenu.getBoundingClientRect().height;

	const containerBottomHeight = containerMainHeight - containerTopHeight;
	const searchListMaxHeight = containerBottomHeight - resultWrapperHeight - sortMenuHeight;
	searchList.setAttribute('style', `max-height: ${searchListMaxHeight}px`);
}

export function setResultsInTheHtmlList (searchResult, container, params = {}) {
	const searchList = container || document.querySelector('.search__result-list');
	if (searchList)
		searchList.innerHTML = '';

	createBuildingsGroups(searchList);

	const listOfCreatedHeaders = [];
	[...document.querySelectorAll('.search__sort-item')].forEach(header => {
		listOfCreatedHeaders.push(header.getAttribute('data-sort-type'));
	});

	let showFloorNumber = Boolean(getFieldsFromAPI.table().find(el => el.type === 'floorNum')) && listOfCreatedHeaders.includes('floorNum');
	let showRoomNumber = Boolean(getFieldsFromAPI.table().find(el => el.type === 'roomNum')) && listOfCreatedHeaders.includes('roomNum');
	const roomNumberExceptions = getFieldsFromAPI.table().find(el => el.type === 'roomNum')?.exceptions && listOfCreatedHeaders.includes('roomNum');
	const balcony = getFieldsFromAPI.table().find(el => el.type === 'balconySize') && listOfCreatedHeaders.includes('balconySize');
	const roofBalcony = getFieldsFromAPI.table().find(el => el.type === 'roofBalconySize') && listOfCreatedHeaders.includes('roofBalconySize');
	const coveredBalcony = getFieldsFromAPI.table().find(el => el.type === 'coveredBalconySize') && listOfCreatedHeaders.includes('coveredBalconySize');
	const basement = getFieldsFromAPI.table().find(el => el.type === 'basementSize') && listOfCreatedHeaders.includes('basementSize');
	const garden = getFieldsFromAPI.table().find(el => el.type === 'gardenSize') && listOfCreatedHeaders.includes('gardenSize');
	const isExposure = getFieldsFromAPI.table().find(el => el.type === 'facing') && listOfCreatedHeaders.includes('facing');
	const isTotalSpace = getFieldsFromAPI.table().find(el => el.type === 'totalSpace') && listOfCreatedHeaders.includes('totalSpace');
	const isRentPrice = getFieldsFromAPI.table().find(el => el.type === 'rentPrice') && listOfCreatedHeaders.includes('rentPrice');
	const isSalePrice = getFieldsFromAPI.table().find(el => el.type === 'salePrice') && listOfCreatedHeaders.includes('salePrice');
	const isPrice2 = getFieldsFromAPI.table().find(el => el.type === 'price2') && listOfCreatedHeaders.includes('price2');
	const isPropPricePerSqm = getFieldsFromAPI.table().find(el => el.type === 'propPricePerSqm') && listOfCreatedHeaders.includes('propPricePerSqm');
	const isMaintenanceFee = getFieldsFromAPI.table().find(el => el.type === 'maintenanceFee') && listOfCreatedHeaders.includes('maintenanceFee');
	const isManagementFee = getFieldsFromAPI.table().find(el => el.type === 'managementFee') && listOfCreatedHeaders.includes('managementFee');
	const isPropNum = getFieldsFromAPI.table().find(el => el.type === 'propNum') && listOfCreatedHeaders.includes('propNum');
	const isBathrooms = getFieldsFromAPI.table().find(el => el.type === 'Bathrooms') && listOfCreatedHeaders.includes('Bathrooms');
	const isPropName = getFieldsFromAPI.table().find(el => el.type === 'propName') && listOfCreatedHeaders.includes('propName');
	const isToilets = getFieldsFromAPI.table().find(el => el.type === 'Toilets') && listOfCreatedHeaders.includes('Toilets');
	const displayPrice = new Price().showPrice;

	for (let {
		engineId,
		modelName,
		propType,
		propNum,
		floorNum,
		roomNum,
		totalSpace,
		salePrice,
		status,
		propName,
		rentPrice,
		price2,
		bmbyPropID,
		bathRooms,
		toilets,
		propPricePerSqm,
		facing,
		accessible,
		tenantPrice,
		treatmentRoom,
		balconySize,
		roofBalconySize,
		coveredBalconySize,
		basementSize,
		gardenSize,
		maintenanceFee,
		managementFee
	} of searchResult) {
		let propNumLabel = '';

		if (Buildings.getBuildingsInfoLength() > 1 && (isMobile || isDesktop)) {
			config.buildings.find(building => {
				if (building.buildingEngineId === engineId.split('_')[0])
					propNumLabel = `(${building.buildingLabel})`;
			});
		}

		const unitCard = UnitCardClass.findCard(engineId)?.html;
		const engineIdActive = (unitCard) ? unitCard.getAttribute('data-engineid') : '';

		const classNames = (engineId !== engineIdActive)
			? ['search__result-item']
			: ['search__result-item', 'active', 'active-card'];

		const currentItemBuildingName = engineId.split('_')[0];
		const buildingGroup = document.querySelector('.search__group-list[building-name="' + currentItemBuildingName + '"]');
		const itemParent = buildingGroup ? buildingGroup : searchList;
		const searchItem = new BaseElement('li')
			.addClass(...classNames)
			.setAttributes({
				'data-bmbyPropID': `${bmbyPropID}`,
				'data-engineId': `${engineId}`,
			})
			.appendTo(itemParent);
		
		if (BmbyMode.active) {
			const colorStatus = config.resources.bundle.selectionBoxes.colorStatus.find(st => st.status === status.toLowerCase());
			if (colorStatus) {
				const colorString = '#' + _color.setStyle(`#${colorStatus.basic}`,LinearSRGBColorSpace).convertLinearToSRGB().getHexString() + '99';
				searchItem.addStyle('background-color',colorString);
			}
		}

		const originContentWrapper = isMobile && new BaseElement()
			.addClass('search__origin-content')
			.appendTo(searchItem.DOMElement);

		if (params.hasOwnProperty('customClass'))
			searchItem.addClass(params['customClass']);

		let maintenanceFeeHtml;
		if (maintenanceFee > 0) {
			maintenanceFeeHtml = `
                <div class="search__result-item--element translate-number price" ${getOrderStyle('maintenanceFee')}>
                    ${numberWithCommas(maintenanceFee, true)}
                </div>`;
		} else {
			maintenanceFeeHtml = `<div class="search__result-item--element"></div>`;
		}

		let managementFeeHtml;
		if (managementFee > 0) {
			managementFeeHtml = `
                <div class="search__result-item--element translate-number price" ${getOrderStyle('managementFee')}>
                    ${numberWithCommas(managementFee, true)}
                </div>`;
		} else {
			managementFeeHtml = `<div class="search__result-item--element"></div>`;
		}

		let rentPriceHtml;
		if (rentPrice > 0) {
			rentPriceHtml = `
                <div class="search__result-item--element translate-number price" ${getOrderStyle('rentPrice')}>
                    ${numberWithCommas(rentPrice, true)}
                </div>`;
		} else {
			rentPriceHtml = `<div class="search__result-item--element"></div>`;
		}

		let propPricePerSqmHTML;
		if (propPricePerSqm > 0) {
			propPricePerSqmHTML = `
                <div class="search__result-item--element translate-number price" ${getOrderStyle('propPricePerSqm')}>
                    ${numberWithCommas(propPricePerSqm, true)}
                </div>`;
		} else {
			propPricePerSqmHTML = `<div class="search__result-item--element"></div>`;
		}

		let balconyHtml;
		if (balcony) {
			if (balcony.visibility === 'check-mark') {
				if (balconySize > 0) {
					balconyHtml = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('balconySize')}></div>`;
				} else {
					balconyHtml = `<div class="search__result-item--element" ${getOrderStyle('balconySize')}>-</div>`;
				}
			} else {
				balconyHtml = `
					<div class="search__result-item--element translate-number" ${getOrderStyle('balconySize')}>
						${numberWithCommas(balconySize)}
					</div>`;
			}
		} else {
			balconyHtml = '';
		}

		let roofBalconyHtml;
		if (roofBalcony) {
			if (roofBalcony.visibility === 'check-mark') {
				if (roofBalconySize > 0) {
					roofBalconyHtml = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('roofBalconySize')}></div>`;
				} else {
					roofBalconyHtml = `<div class="search__result-item--element" ${getOrderStyle('roofBalconySize')}>-</div>`;
				}
			} else {
				roofBalconyHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('roofBalconySize')}>${numberWithCommas(roofBalconySize)}</div>`;
			}
		} else {
			roofBalconyHtml = '';
		}

		let coveredBalconyHtml;
		if (coveredBalcony) {
			if (coveredBalcony.visibility === 'check-mark') {
				if (coveredBalconySize > 0) {
					coveredBalconyHtml = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('coveredBalconySize')}></div>`;
				} else {
					coveredBalconyHtml = `<div class="search__result-item--element" ${getOrderStyle('coveredBalconySize')}>-</div>`;
				}
			} else {
				coveredBalconyHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('coveredBalconySize')}>${numberWithCommas(coveredBalconySize)}</div>`;
			}

		} else {
			coveredBalconyHtml = '';
		}

		let basementHtml;
		if (basement) {
			if (basement.visibility === 'check-mark') {
				if (basementSize > 0) {
					basementHtml = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('basementSize')}></div>`;
				} else {
					basementHtml = `<div class="search__result-item--element" ${getOrderStyle('basementSize')}>-</div>`;
				}
			} else {
				basementHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('basementSize')}>${numberWithCommas(basementSize)}</div>`;
			}
		} else {
			basementHtml = '';
		}

		let propNameHtml;
		if (isPropName) {
			propNameHtml = `<div class="search__result-item--element" ${getOrderStyle('propName')}>${propName}</div>`;
		} else {
			propNameHtml = '';
		}

		let gardenHtml;
		if (garden) {
			if (garden.visibility === 'check-mark') {
				if (gardenSize > 0) {
					gardenHtml = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('gardenSize')}></div>`;
				} else {
					gardenHtml = `<div class="search__result-item--element" ${getOrderStyle('gardenSize')}>-</div>`;
				}
			} else {
				gardenHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('gardenSize')}>${numberWithCommas(gardenSize)}</div>`;
			}
		} else {
			gardenHtml = '';
		}

		let price2Html;
		let crossPrice = '';
		if (price2 > 0 && isPrice2) {
			crossPrice = 'search__old-price-search-element';
			price2Html = `
                <div class="search__result-item--element translate-number price" ${getOrderStyle('price2')}>
                    ${numberWithCommas(price2, true)}
                </div>`;
		} else {
			price2Html = `<div class="search__result-item--element"></div>`;
		}

		let priceHTML;
		if (salePrice === 0) {
			priceHTML = `
                <div class="search__result-item--element price"></div>`;
		} else {
			priceHTML = `
                <div class="search__result-item--element translate-number price ${crossPrice}" ${getOrderStyle('salePrice')}>
                    ${numberWithCommas(salePrice, true)}
                </div>`;
		}

		const exposure = (facing.length && isExposure) ? facing.map(item => /^[A-Za-z]*$/.test(item) ? item : facingDictionary()[item].en) : [];
		const exposureHTML = (exposure.length)
			? `<div class="search__result-item--element" ${getOrderStyle('facing')}>${exposure.map(item => `<span class="language-string" data-dictionary="${item}Short"> ${changeLangToCurrent(`${item}Short`)}</span>`)}</div>`
			: '<div class="search__result-item--element"></div>';

		let accessibleHTML = '';
		let treatmentRoomHTML = '';
		let tenantPriceHTML = '';
		let accessibleField = false;
		let treatmentRoomField = false;
		let tenantPriceField = false;
		let modelNameField = false;
		let propTypeField = false;
		// const positiveHTML = `<div class="search__result-item--element icon-check-mark"></div>`;
		// const negativeHTML = `<div class="search__result-item--element">-</div>`;

		if (getFieldsFromAPI.table().find(state => state.type === 'accessible') && listOfCreatedHeaders.includes('accessible'))
			accessibleField = true;
		if (getFieldsFromAPI.table().find(state => state.type === 'treatmentRoom' || state.type === 'Treatment room') && (listOfCreatedHeaders.includes('treatmentRoom') || listOfCreatedHeaders.includes('Treatment room')))
			treatmentRoomField = true;
		if (getFieldsFromAPI.table().find(state => state.type === 'tenantPrice') && listOfCreatedHeaders.includes('tenantPrice'))
			tenantPriceField = true;
		if (getFieldsFromAPI.table().find(state => state.type === 'modelName' || state.type === 'Model') && (listOfCreatedHeaders.includes('modelName') || listOfCreatedHeaders.includes('Model')))
			modelNameField = true;
		if (getFieldsFromAPI.table().find(state => state.type === 'propType') && listOfCreatedHeaders.includes('propType'))
			propTypeField = true;

		if (accessibleField) {
			if (!!accessible) {
				accessibleHTML = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('accessible')}></div>`;
			} else {
				accessibleHTML = `<div class="search__result-item--element" ${getOrderStyle('accessible')}>-</div>`;
			}
		}

		if (treatmentRoomField) {
			const orderStyle = getOrderStyle('treatmentRoom') || getOrderStyle('Treatment room');
			if (!!treatmentRoom && treatmentRoomField) {
				treatmentRoomHTML = `<div class="search__result-item--element icon-check-mark" ${orderStyle}></div>`;
			} else {
				treatmentRoomHTML = `<div class="search__result-item--element" ${orderStyle}>-</div>`;
			}
		}

		if (tenantPriceField) {
			if (!!tenantPrice && tenantPriceField) {
				tenantPriceHTML = `<div class="search__result-item--element icon-check-mark" ${getOrderStyle('tenantPrice')}></div>`;
			} else {
				tenantPriceHTML = `<div class="search__result-item--element" ${getOrderStyle('tenantPrice')}>-</div>`;
			}
		}

		const isInFavorite = getObjectFromLocalStorage(engineId);
		if (isInFavorite) {
			const {selectedMesh} = findSelectionAndCrmData(isInFavorite.engineId);
			new Favorite(selectedMesh, 'add');
		}

		const toggleClassFav = isInFavorite ? 'icon-favorite_selected' : 'icon-unselected';
		const iconFavorite = `<span class="search__favorites-icon ${toggleClassFav}" data-favorite="favorite"></span>`;
		const favoriteHTML = status === AVAILABLE_STATUS ? `<div class="search__result-item--element" data-item-type="favorite" ${getOrderStyle('favorite')}>${iconFavorite}</span></div>` : `<div></div>`;

		if (params.hasOwnProperty('template')) {
			const favTemp = params.template['favorite'] ? favoriteHTML : '';
			const propNumTemp = params.template['aptNumber'] ? `<div class="search__result-item--element">${isMobile && params.template['favorite'] ? iconFavorite : ''}<span data-type="propNum">${propNum}${propNumLabel}</span></div>` : '';
			const exposureTemp = params.template['exposure'] ? exposureHTML : '';
			const floorNumTemp = params.template['floorNumber'] ? `<div class="search__result-item--element">${floorNum}</div>` : '';

			let roomNumTemp;

			if (params.template['roomNumber']) {
				if (roomNum === 0) {
					roomNumTemp = `<div class="search__result-item--element language-string" data-dictionary="Studio">${changeLangToCurrent('Studio')}</div>`;
				} else {
					roomNumTemp = `<div class="search__result-item--element">${roomNum}</div>`;
				}
			} else {
				roomNumTemp = '';
			}

			const totalSpaceTemp = params.template['totalSpace']
				? `<div class="search__result-item--element translate-number">${numberWithCommas(parseInt(totalSpace))}</div>`
				: '';
			const priceTemp = params.template['price'] ? priceHTML : '';

			const wrapperElement = isMobile ? originContentWrapper.DOMElement : searchItem.DOMElement;
			wrapperElement.insertAdjacentHTML('afterbegin', `
            ${favTemp}
            ${propNumTemp}
            ${exposureTemp}
            ${floorNumTemp}
            ${roomNumTemp}
            ${totalSpaceTemp}
            ${priceTemp}
            ${rentPriceHtml}
            ${propPricePerSqmHTML}
            ${price2Html}
            ${maintenanceFeeHtml}
            ${managementFeeHtml}
            `);
		} else {
			let roomNumHtml;
			if (showRoomNumber) {
				if (roomNumberExceptions) {
					const value = roomNumberExceptions[roomNum];
					roomNumHtml = `<div class="search__result-item--element language-string" data-dictionary="${value} ${getOrderStyle('roomNum')}">${changeLangToCurrent(value)}</div>`;
				} else if (roomNum === 0) {
					roomNumHtml = `<div class="search__result-item--element language-string" data-dictionary="Studio" ${getOrderStyle('roomNum')}>${changeLangToCurrent('Studio')}</div>`;
				} else {
					roomNumHtml = `<div class="search__result-item--element" ${getOrderStyle('roomNum')}>${roomNum}</div>`;
				}
			} else {
				roomNumHtml = '<div class="search__result-item--element"></div>';
			}

			let bathroomsHtml;
			if (isBathrooms) {
				if (bathRooms > 0) {
					bathroomsHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('Bathrooms')}>${bathRooms}</div>`;
				} else {
					bathroomsHtml = `<div class="search__result-item--element" ${getOrderStyle('Bathrooms')}>-</div>`;
				}
			} else {
				bathroomsHtml = '';
			}

			let toiletsHtml;
			if (isToilets) {
				if (toilets > 0) {
					toiletsHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('Toilets')}>${toilets}</div>`;
				} else {
					toiletsHtml = `<div class="search__result-item--element" ${getOrderStyle('Toilets')}>-</div>`;
				}
			} else {
				toiletsHtml = '';
			}

			const isGroundFloor = settingsData.groundFloor.isActive && (parseInt(settingsData.groundFloor.floor) === parseInt(floorNum));
			const shortLabel = settingsData.groundFloor.shortLabel;
			let floorNumHTML = `<div class="search__result-item--element" ${getOrderStyle('floorNum')}>${floorNum}</div>`;
			if (isGroundFloor) {
				floorNumHTML = new BaseElement()
					.addClass('search__result-item--element', 'language-string')
					.innerHtml(changeLangToCurrent(shortLabel))
					.addAttribute('data-dictionary', shortLabel);
				if (!isMobile) {
					floorNumHTML.addStyle('order', `${getOrder('floorNum')}`);
				}
				floorNumHTML = floorNumHTML.DOMElement.outerHTML;
			}

			const modelOrderStyle = getOrderStyle('Model') || getOrderStyle('modelName')
			const modelNameHtml = `<div class="search__result-item--element" ${modelOrderStyle}>${modelName}</div>`;
			const propTypeHtml = `<div class="search__result-item--element" ${getOrderStyle('propType')}>${changeLangToCurrent(propType)}</div>`;
			const totalSpaceHtml = `<div class="search__result-item--element translate-number" ${getOrderStyle('totalSpace')}>${numberWithCommas(totalSpace)}</div>`;

			let propNumHtml = `<div data-type="propNum" ${getOrderStyle('propNum')}>${propNum}${propNumLabel}</div>`;
			propNumHtml = `
                        <div data-type="propNum" ${getOrderStyle('propNum')}>
                            <span>${propNum}</span>
                        </div>`;

			const wrapperElement = isMobile ? originContentWrapper.DOMElement : searchItem.DOMElement;
			wrapperElement.insertAdjacentHTML('afterbegin', `
            ${favoriteHTML}
            ${isPropNum ? propNumHtml : ''}
            ${modelNameField ? modelNameHtml : ''}
            ${isExposure ? exposureHTML : ''}
            ${showFloorNumber ? floorNumHTML : ''}
            ${showRoomNumber ? roomNumHtml : ''}
            ${propTypeField ? propTypeHtml : ''}
            ${isTotalSpace ? totalSpaceHtml : ''}
            ${(isSalePrice && displayPrice) ? priceHTML : ''}
            ${(isRentPrice && displayPrice) ? rentPriceHtml : ''}
            ${(isPropPricePerSqm && displayPrice) ? propPricePerSqmHTML : ''}
            ${(isManagementFee && displayPrice) ? managementFeeHtml : ''}
            ${(isMaintenanceFee && displayPrice) ? maintenanceFeeHtml : ''}
            ${(isPrice2 && displayPrice) ? price2Html : ''}
            ${accessibleHTML}
            ${treatmentRoomHTML}
            ${roofBalconyHtml}
            ${bathroomsHtml}
            ${propNameHtml}
            ${basementHtml}
            ${tenantPriceHTML}
            ${balconyHtml}
            ${coveredBalconyHtml}
            ${gardenHtml}
            ${toiletsHtml}
          `);
		}
		if (itemParent)
			itemParent.appendChild(searchItem.DOMElement);
	}
	Favorite.intersect();

	const emptyBuildingGroups = [...document.querySelectorAll('.search__group-list:empty')];
	emptyBuildingGroups.forEach(group => {
		group.closest('.search__building-group').remove();
	});
}

function getOrder (type) {
	if (isMobile)
		return undefined;

	const configProp = getFieldsFromAPI.table().find(state => state.type === type);
	if (!configProp)
		return undefined;

	return configProp.order;
}

function getOrderStyle (type) {
	if (isMobile)
		return '';

	const order = getOrder(type);
	if (!order)
		return '';

	return `style="order: ${order};"`;
}

function createBuildingsGroups (searchList) {
	if (Buildings.getBuildingsInfoLength() < 2)
		return;

	Buildings.getBuildingsArray().forEach((building) => {
		if (Object.keys(building.info).length === 0 || building.apartments.length < 2)
			return;

		const state = _collapsedGroups.includes(building.name) ? 'collapsed' : 'expanded';
		const group = new BaseElement()
			.addClass('search__building-group')
			.setAttributes({
				state,
				'building-name': building.name,
			})
			.appendTo(searchList);
		const title = new BaseElement()
			.addClass('search__group-title')
			.appendTo(group.DOMElement);
		const buildingNameWrapper = new BaseElement()
			.addClass('building-group__building-wrapper')
			.appendTo(title.DOMElement);

		const towerName = getTowerName(building);
		if (towerName.split(' ').length < 2) {
			new BaseElement('span')
				.addClass(`building-group__building-prefix`, 'language-string')
				.addAttribute('data-dictionary', 'building')
				.innerHtml(changeLangToCurrent('building'))
				.appendTo(buildingNameWrapper.DOMElement, 'beforeend');
		}

		const buildingName = new BaseElement('span')
			.addClass('building-group__building-name', 'language-string')
			.innerHtml(towerName)
			.appendTo(buildingNameWrapper.DOMElement);
		building.info.towerName.forEach(name => {
			buildingName.addAttribute(`data-lang-${name.lang}`, name.value);
		});

		new BaseElement('span')
			.addClass('icon-down2', 'building-group__checker')
			.appendTo(title.DOMElement);
		new BaseElement('ul')
			.addClass('search__group-list')
			.setAttributes({
				'building-name': building.name,
			})
			.appendTo(group.DOMElement);
	});
}

/**
 * Search handlers
 */

export async function openSearchHandler () {
	if (settingsData.activeViewMode === ViewMode.Orbit)
		return;

	const unitCard = UnitCardClass.findCard()?.html;
	if (!settingsData.switchLang && unitCard)
		UnitCardClass.closeMultipleCards();

	const searchModule = createSearchModule();
	hideTooltip();
	searchGetResults();
	if (!isMobile)
		searchModule.addAttribute('state', 'opened');
	setParametersDependsOnCanvasArea('building-switcher');

	if (isWeb && !isNoMenu) {
		const mainContainer = document.querySelector('.search__container-main');
		const asideContainer = document.querySelector('.aside__container');
		mainContainer ? mainContainer.style.marginInlineStart = '56px' : null;
		asideContainer ? asideContainer.style.position = 'fixed' : null;
	}

	// Remove the timeout and use the animation end event instead
	if (!isMobile) {
		const containerMain = UIRoot.querySelector('.search__container-main');
		containerMain.addEventListener('animationend', () => {
			setSearchContainerMaxHeight();
		}, { once: true });
	}
}

function checkBoxHandler({target}) {
	const {searchState} = settingsData;
	const type = target.getAttribute('data-type');
	const isRangeCheckbox = target.getAttribute('data-is-range');
	let checkedValue = target.getAttribute(`data-${type.toLowerCase()}`);
	checkedValue = (!isNumeric(checkedValue))
		? checkedValue
		: parseFloat(checkedValue);

	const stateItemOfCheckedType = searchState.find(data => data.type === type);
	const indexOfCheckedValueInState = stateItemOfCheckedType.checked.value.indexOf(checkedValue);

	if ((type === 'accessible' || type === 'treatmentRoom') && target.checked)
		checkedValue = 1;

	if (/*type === 'roomNum' && */typeof checkedValue === 'string' && checkedValue.match(/\+/g) && !isRangeCheckbox) {
		const selectedValue = Number(checkedValue.replace('+', ''));
		if (target.checked) {
			//More than 6
			checkedValue = [...new Set(settingsData.allCrmData.filter(apt => apt[type] >= selectedValue).map(apt => apt[type]))];
		} else {
			checkedValue = stateItemOfCheckedType.checked.value.filter(num => num < selectedValue);
		}
	}

	if (isRangeCheckbox) {
		const isValueWithPlus = checkedValue.indexOf('+') !== -1;
		const minValue = isValueWithPlus
			? parseFormattedNumber(checkedValue.slice(0, -1))
			: parseFormattedNumber(checkedValue.split('-')[0].trim());
		const maxValue = !isValueWithPlus && parseFormattedNumber(checkedValue.split('-')[1].trim());
		if (target.checked) {
			checkedValue = [...new Set(settingsData.allCrmData
				.filter(apt => isValueWithPlus
					? minValue <= apt[type]
					: minValue <= apt[type] && apt[type] <= maxValue)
				.map(apt => apt[type]))
			];
		} else {
			checkedValue = stateItemOfCheckedType.checked.value.filter(item => isValueWithPlus
				? item < minValue
				: item < minValue || item > maxValue);
		}
	}

	target.checked && indexOfCheckedValueInState === -1
		? Array.isArray(checkedValue)
			? stateItemOfCheckedType.checked.value.push(...checkedValue)
			: stateItemOfCheckedType.checked.value.push(checkedValue)
		: Array.isArray(checkedValue)
			? stateItemOfCheckedType.checked.value = checkedValue
			: stateItemOfCheckedType.checked.value.splice(indexOfCheckedValueInState, 1);

	if (!components.mobileSearch) {
		searchGetResults();
		updateMultiSearch();
	} else {
		components.mobileSearch.predict();
	}


	stateItemOfCheckedType.checked.value.length > 0
		? target.closest('.search__option').setAttribute('state', 'active')
		: target.closest('.search__option').setAttribute('state', 'inactive');

	// if (isMobile) {
	// 	FiltersCounter.update();
	// }
}

export function changeActiveCardWhenBuildingClick (clickedSelectionBox, switchCard) {
	const dataEngineID = clickedSelectionBox.name;
	const searchItem = document.querySelector(`li.search__result-item:not(.similar-item)[data-engineid=${dataEngineID}]`);
	const searchItemActiveCard = document.querySelector('.search__result-item:not(.similar-item).active-card');

	if (searchItemActiveCard) {
		!switchCard && searchItemActiveCard.classList.remove('active-card');
		if (!cardsData.multiplyCards && UnitCardClass.cards.length === 1 || switchCard) {
			searchItemActiveCard.classList.remove('active');
		} else if (cardsData.multiplyCards && UnitCardClass.cards.length > 1 || switchCard) {
			searchItemActiveCard.classList.remove('active');
		}
	}

	if (!searchItem)
		return;

	searchItem.classList.add('active');
	searchItem.classList.add('active-card');

	searchItem.scrollIntoView({
		behavior: 'smooth',
		block: 'center',
	});
}

export async function handleCardSelect (event) {
	let target = event.target.closest('.search__result-item');

	if (settingsData.bombBlock || userPrevent() || settingsData.activeModeSwitching || !target)
		return;

	settingsData.activeModeSwitching = true;

	if (target.classList.contains('search__result-list'))
		return;

	let dataEngineID = target.getAttribute('data-engineid');
	const targetedBuildingName = dataEngineID.split('_')[0];

	if ((Buildings.activeBuilding.name !== targetedBuildingName) && (!projectMode.LiteMode.modeIs('picture') && !projectMode.LiteMode.modeIs('empty'))) {
		if (settingsData.buildingDestroyed)
			await projectMode.FloorPlan.exitFromMode();
		userPrevent(false);
		await projectMode.Cylinder.switchBuilding(targetedBuildingName, dataEngineID);
	}

	if (!dataEngineID) {
		target = target.closest('.search__result-item');
		dataEngineID = target.getAttribute('data-engineid');
	}

	const isTargetFavorite = event.target.getAttribute('data-favorite') === 'favorite';
	if (isTargetFavorite && dataEngineID) {
		handleFavoriteLogic(event, 'searchClick');
		settingsData.activeModeSwitching = false;
		return;
	}

	const unitCard = UnitCardClass.findCard()?.html;
	if (cardsData.multiplyCards) {
		const cardFromAnotherBuilding = UnitCardClass.cards.some(card => card.html.classList.contains('card-from-another-building'));
		if (!cardFromAnotherBuilding)
			hoverOutPreviousSelection(unitCard);
	} else {
		hoverOutPreviousSelection(unitCard);
	}

	const previousTarget = UIRoot.querySelector('.search__result-item.active');
	previousTarget && previousTarget.classList.remove('active');
	target.classList.add('active');

	markSelectedApartments(dataEngineID);
	const {selectedMesh} = findSelectionAndCrmData(dataEngineID);
	flatClick(selectedMesh);

	settingsData.activeModeSwitching = false;
}

export function toggleClassFavoritesInSearch (event, isInFavorite, similarSearch) {
	if (event.target)
		setActiveClassHandler(event);

	if (!event) {
		event = document.querySelector('.search__result-item.active-card span[data-favorite=favorite]');
	} else if (similarSearch) {
		event = event;
	} else {
		event = event.target;
	}

	const isNotSelected = event.classList.contains('icon-unselected');

	if (isNotSelected && !isInFavorite) {
		event.classList.remove('icon-unselected');
		event.classList.add('icon-favorite_selected');
	} else {
		event.classList.add('icon-unselected');
		event.classList.remove('icon-favorite_selected');
	}
}

export function markSelectedApartments (engineIdActive) {
	const target = document.querySelector('.search__result-item.active');
	let engineId;

    if (isMobile) {
        // if (engineIdActive === settingsData.lastClickedFlat.name)
        //     return;
        engineId = engineIdActive;
        hoverOutPreviousSelection(null,{name: engineIdActive});
    } else {
        if (!target)
            return;

        engineId = (!engineIdActive) ? target.getAttribute('data-engineid') : engineIdActive;
    }

    if (!engineId)
        return;

	const {selectedMesh} = findSelectionAndCrmData(engineId);
	settingsData.lastClickedFlat = selectedMesh;
	selectedMesh.userData.isClicked = true;

	if (config.features.privateHouseMode) {
		selectedMesh.userData.isActive = false;
		selectedMesh.userData.isInSearch = false;
		selectionBoxesSetStatusColor(selectedMesh, 'unlock');
	} else {
		selectionBoxesSetStatusColor(selectedMesh, 'click');
	}

	target && target.scrollIntoView({
		behavior: 'smooth',
		block: 'center',
	});
	settingsData.switchLang = false;
}

function handleSearchSort (event) {
	const searchSort = document.querySelector('.search__sort');
	const currentSortItem = event.target.closest('.search__sort-item');
	if (!currentSortItem)
		return;
	const sortAttribute = currentSortItem.getAttribute('data-sort-type');
	let {searchResult} = settingsData;
	const searchSortState = getFieldsFromAPI.table();

	if (currentSortItem.classList.contains('plug') || !sortAttribute)
		return;

	if (!isMobile) {
		const arrowsUp = searchSort.querySelectorAll('span[class=icon-up]');
		arrowsUp.forEach(arrow => {
			const parent = arrow.parentNode;
			if (parent !== currentSortItem) {
				arrow.classList.remove('icon-up');
				arrow.classList.add('icon-down');

				parent.setAttribute('data-sort-state', 'minmax');
			}
		});
	}

	const items = searchSort.querySelectorAll('.search__sort-item');
	items.forEach(item => {
		item === currentSortItem
			? !item.classList.contains('active')
			? item.classList.add('active')
			: null
			: item.classList.contains('active')
			? item.classList.remove('active')
			: null;
	});

	const dataValue = currentSortItem.getAttribute('data-sort-state');
	searchSortState.forEach(item => item.isActive = false);
	const stateItem = searchSortState.find(item => item.type === sortAttribute);

	stateItem.state =
		dataValue === 'minmax'
			? 'maxmin'
			: 'minmax';

	const arrowClassName =
		dataValue === 'minmax'
			? `span[class=icon-down]`
			: `span[class=icon-up]`;

	const arrow = currentSortItem.querySelector(`${arrowClassName}`);
	arrow.classList.toggle('icon-down');
	arrow.classList.toggle('icon-up');

	stateItem.isActive = true;
	currentSortItem.setAttribute('data-sort-state', `${stateItem.state}`);

	searchResult = sortArray(stateItem.state, sortAttribute, searchResult);
	setTimeout(() => markSelectedApartments(), 500);

	setResultsInTheHtmlList(searchResult);
}

export function sortArray (value, type, sortArray) {
	const copyArray = sortArray.slice();

	switch (type) {
		case 'facing': {
			const lang = getCurrentLang();

			copyArray.sort((a, b) => a[type][0]?.localeCompare(b[type][0], `${lang}`, {ignorePunctuation: true}));
			const checkEmptyFacing = copyArray.some(el => el.facing.length === 0);

			if (checkEmptyFacing)
				copyArray.sort((prev, next) => next.facing.length - prev.facing.length);

			return value === 'minmax' ? copyArray : copyArray.reverse();
		}
		case 'favorite': {
			return sortFavorites(value, copyArray);
		}
		case 'propNum': {
			return value === 'minmax' ? copyArray.sort((a, b) => parseInt(b[type]) - parseInt(a[type])) : copyArray.sort((a, b) => parseInt(a[type]) - parseInt(b[type]));
		}
		case 'modelName': {
			return value === 'minmax' ? copyArray.sort((a, b) => b[type].localeCompare(a[type])) : copyArray.sort((a, b) => a[type].localeCompare(b[type]));
		}
		case 'propType': {
			return value === 'minmax' ? copyArray.sort((a, b) => b[type].localeCompare(a[type])) : copyArray.sort((a, b) => a[type].localeCompare(b[type]));
		}
		default: {
			return value === 'minmax'
				? copyArray.sort((a, b) => b[type] - a[type])
				: copyArray.sort((a, b) => a[type] - b[type]);
		}
	}
}

function sortFavorites (condition, sortArray) {
	let localFavorites;
	if (typeof window.localStorage !== 'undefined') {
		localFavorites = JSON.parse(localStorage.getItem('favorites'));
	} else {
		localFavorites = JSON.parse(Cookie.getCookie('favorites'));
	}

	if (!localFavorites || localFavorites?.length === 0)
		return sortArray;

	return sortArray.reduce((newResultsArray, item) => {
		const isInFavorites = localFavorites.find(fav => fav.engineId === item.engineId);
		if (newResultsArray.indexOf(item) === -1) {
			if (condition === 'maxmin') {
				isInFavorites ? newResultsArray.unshift(item) : newResultsArray.push(item);
			} else {
				!isInFavorites ? newResultsArray.unshift(item) : newResultsArray.push(item);
			}
		}
		return newResultsArray;
	}, []);
}

export function scrollSearchListToActiveBuildingGroup (buildingName) {
	if (Buildings.getBuildingsInfoLength() <= 1)
		return;

	const buildingGroupElement = document.querySelector(`.search__building-group[building-name="${buildingName}"]`);

	if (buildingGroupElement)
		buildingGroupElement.scrollIntoView({behavior: 'smooth'});
}

export function toggleResultsList (action) {
	if (isMobile && action !== 'close')
		openSearchHandler();

	const btn = document.querySelector('.cylinder-control-panel__button.result-list');
	const containerBottom = document.querySelector('.search__container-bottom');
	if (!btn || !containerBottom)
		return;

	// const span = btn.querySelector('.icon-building') || btn.querySelector('.icon-list1');
	const isOpened = containerBottom.classList.contains('opened');
	if (action === 'close' && !isOpened)
		return;

	setSearchContainerMaxHeight();

	if (settingsData.isLandscape) {
		if (isOpened) {
			gsap.to(containerBottom, {
				top: isMobile ? '100%' : 500,
				duration: 0.2,
				onStart: () => {
					switchResultListButtonAppearanceTo('list');
				},
				onComplete: () => {
					containerBottom.classList.remove('opened');
				},
			});
		} else {
			gsap.to(containerBottom, {
				top: 0,
				duration: 0.2,
				onStart: () => {
					switchResultListButtonAppearanceTo('building');
				},
				onComplete: () => {
					containerBottom.classList.add('opened');
				},
			});
		}
		return;
	}

	if (isOpened) {
		gsap.to(containerBottom, {
			duration: 0.5,
			top: '100%',
			onStart: () => {
				switchResultListButtonAppearanceTo('list');
			},
			onComplete: () => {
				containerBottom.classList.remove('opened');
				settingsData.searchListOpened = false;
			},
		});
	} else {
		gsap.to(containerBottom, {
			duration: 0.5,
			top: '0',
			onStart: () => {
				switchResultListButtonAppearanceTo('building');

				const unitCard = document.querySelector('.unit-card');
				if (unitCard) {
					const unitCardHeight = unitCard.getBoundingClientRect().height;
					const menu = document.querySelector('.additional-menu__container');
					const menuHeight = menu.getBoundingClientRect().height;
					const searchResultList = containerBottom.querySelector('.search__result-list');
					searchResultList.style.paddingBottom = `${unitCardHeight + menuHeight}px`;
				}
			},
			onComplete: () => {
				containerBottom.classList.add('opened');
				settingsData.searchListOpened = true;
			},
		});
	}
}

const switchResultListButtonAppearanceTo = (appearance) => {
	const resultListButton = document.querySelector('.cylinder-control-panel__button.result-list');
	const iconElement =
		resultListButton.querySelector('.icon-building') ||
		resultListButton.querySelector('.icon-list1');
	const textElement = resultListButton.querySelector('.language-string.text');

	switch (appearance) {
		case 'list':
			iconElement.classList.remove('icon-building');
			iconElement.classList.add('icon-list1');

			textElement.setAttribute('data-dictionary', 'List');
			textElement.innerHTML = changeLangToCurrent('List');
			break;
		case 'building':
			iconElement.classList.remove('icon-list1');
			iconElement.classList.add('icon-building');

			textElement.setAttribute('data-dictionary', 'Model');
			textElement.innerHTML = changeLangToCurrent('Model');
			break;
		default:
			console.log('switchResultListButtonAppearanceTo default case');
			break;
	}
};

let keyPressed = false;
let range;
let stateValue;
let prevValue;

function handleRangeInputs (event) {
	const target = event.target;
	const type = target.getAttribute('data-type');
	const ranges = document.querySelectorAll(`.search__range-input--${type}`);
	const {searchState} = settingsData;
	const currentState = searchState.find(item => item.type === type);

	ranges.forEach(item => {
		if (item.classList.contains('search__range-input--one') && target.classList.contains('search__option-input--min')) {
			range = item;
			stateValue = 'min';
		} else if (item.classList.contains('search__range-input--two') && target.classList.contains('search__option-input--max')) {
			range = item;
			stateValue = 'max';
		}
	});

	const rangeTrack = range.parentNode.querySelector('.search__range-slider-track');
	const rangeOne = range.parentNode.querySelector('.search__range-input--one');
	const rangeTwo = range.parentNode.querySelector('.search__range-input--two');

	switch (event.type) {
		case 'keyup': {
			if (event.key === 'Enter') {
				setInputValue();
				document.activeElement.blur();
			}

			break;
		}
		case 'focus': {
			if (isMobile) {
				settingsData.stopWindowResizeEvents = true;
			}
			prevValue = target.value;
			target.value = '';
			keyPressed = false;
			break;
		}
		case 'focusout': {
			if (!target.value) {
				target.value = prevValue.replace(/,/g, '');
			}

			if (isMobile) {
				setTimeout(() => {
					settingsData.stopWindowResizeEvents = false;
				}, 200);
			}
			!keyPressed && setInputValue();
			break;
		}
		case 'change': {
			input();
			break;
		}
	}

	function input () {
		const reg = new RegExp('^[0-9]+$');
		if (!reg.test(target.value)) {
			target.value = prevValue && prevValue.replace(/,/g, '');
			document.activeElement.blur();
			return;
		}

		if (range === rangeOne && +target.value < +range.getAttribute('min')) {
			target.value = +range.getAttribute('min');
		} else if (range === rangeTwo && +target.value > +range.getAttribute('max')) {
			target.value = +range.getAttribute('max');
		}
	}

	function setInputValue () {
		keyPressed = true;
		if (!target.value) {
			range.value = currentState[stateValue];
			target.value = numberWithCommas(`${currentState[stateValue]}`, true);
		}

		if (range === rangeOne && +target.value >= +rangeTwo.value) {
			target.value = +rangeTwo.value;
		} else if (range === rangeTwo && +target.value <= +rangeOne.value) {
			target.value = +rangeOne.value;
		}

		range.value = +target.value;
		currentState[stateValue] = +target.value;
		target.value = numberWithCommas(target.value, true);
		range.setAttribute('value', target.value);

		if (parseInt(rangeTwo.value) - parseInt(rangeOne.value) <= 0) {
			if (range === rangeOne) {
				rangeOne.style.zIndex = 2;
				rangeTwo.style.zIndex = 1;
			} else {
				rangeOne.style.zIndex = 1;
				rangeTwo.style.zIndex = 2;
			}
		}

		fillTrackColor(rangeTrack, rangeOne, rangeTwo);
		if (!components.mobileSearch) {
			searchGetResults();
			updateMultiSearch();
		} else {
			components.mobileSearch.predict();
		}
	}
}

/**
 * Close and remove search block
 */

export async function removeSearchContainer (action, option) {
	const parentContainer = document.querySelector('.search__container-main');
	if (!parentContainer)
		return;

	const activeBuilding = Buildings.getActiveBuilding();
	activeBuilding.apartments.forEach(item => {
		if (settingsData.buildingDestroyed) {
			if (item.userData.floor !== settingsData.currentFloor && item.userData.isActive)
				selectionBoxesSetStatusColor(item, 'unlock');
		} else {
			if (item.userData.isActive)
				selectionBoxesSetStatusColor(item, 'unlock');
		}
	});

	const unitCard = UnitCardClass.findCard()?.html;
	if (unitCard && option !== 'building-switcher') {
		const engineId = unitCard.getAttribute('data-engineid');
		const activeApartment = activeBuilding.apartments.find(apt => apt.name === engineId);
		selectionBoxesSetStatusColor(activeApartment, 'click');
	}

	if (action === 'immediately')
		parentContainer.remove();

	if (!isMobile)
		parentContainer.setAttribute('state', 'closed');

	setParametersDependsOnCanvasArea('building-switcher');
}

/**
 * Animations
 */

export function updateComponentsOnCanvasResize () {
    if (!threeJsVariables.canvasResize)
        return;
	const rootHeight = TRoot.getBoundingClientRect().height;
	const rootWidth = TRoot.getBoundingClientRect().width;

	threeJsVariables.perspectiveCamera.aspect = rootWidth / rootHeight;
	threeJsVariables.perspectiveCamera.updateProjectionMatrix();
	threeJsVariables.labelRenderer?.setSize(rootWidth, rootHeight);
	threeJsVariables.renderer.setSize(rootWidth, rootHeight);
	threeJsVariables.composer.setSize(rootWidth, rootHeight);
	if (Vantages.scopes.length === 0)
		threeJsVariables.composer.composer.render();

	projectMode.LiteMode.resizeContainer();
	showOrHideCylinderFloorNumbers();
	projectMode.FloorPlan?.floorSlider?.resize();
	projectMode.AirMode?.resize(rootWidth, rootHeight);
}

const addWrapperAndTextToSortItems = () => {
	const searchSortItems = [...document.getElementsByClassName('search__sort-item')];
	searchSortItems.forEach(item => {
		const currentSortItem = item.getAttribute('data-sort-type');
		if (isMobile && (currentSortItem === 'salePrice' || currentSortItem === 'rentPrice' || currentSortItem === 'price2'))
			return;

		if (!item.querySelector('.search__result-icon-wrapper')) {
			const itemIconWrapper = document.createElement('div');
			itemIconWrapper.classList.add('search__result-icon-wrapper');
			item.appendChild(itemIconWrapper);

			const searchResultsSortItemText = document.createElement('span');
			const hideTextForList = ['propNum', 'favorite'];
			searchResultsSortItemText.classList.add('icon-text', 'language-string', 'text', !isMobile && hideTextForList.includes(currentSortItem) ? 'hidden' : null);
			let closestDataSortType = item.closest('[data-sort-type]').getAttribute('data-sort-type');
			switch (closestDataSortType) {
				case 'modelName':
					closestDataSortType = 'Model';
					break;
				case 'roomNum':
					closestDataSortType = 'rooms';
					break;
				case 'floorNum':
					closestDataSortType = 'floor';
					break;
				case 'salePrice':
					closestDataSortType = 'rentPrice';
					break;
				case 'favorite':
					closestDataSortType = '';
					break;
				case 'propNum':
					closestDataSortType = 'aptNumber';
					break;
				case 'bathRooms':
					closestDataSortType = 'bathrooms';
					break;
				case 'coveredBalconySize':
					closestDataSortType = 'covered balcony size';
					break;
			}

			// if (closestDataSortType === 'favorite') {
			// 	closestDataSortType = '';
			// }

			searchResultsSortItemText.setAttribute('data-dictionary', closestDataSortType);
			searchResultsSortItemText.innerHTML = changeLangToCurrent(closestDataSortType);

			const currentItemIconElement = item.querySelector('[data-type="icon"]') || item.querySelector('.search__sort-currency');
			if (currentItemIconElement)
				itemIconWrapper.appendChild(currentItemIconElement);
			itemIconWrapper.appendChild(searchResultsSortItemText);
		}
	});
};

export function resetWebFilters() {
	const allCheckboxes = [...document.querySelectorAll('.search__option-input[type="checkbox"]')];
	allCheckboxes.forEach(checkbox => {
		if (checkbox.checked)
			checkbox.click();
	});

	const allSwitches = [...document.querySelectorAll('.switch__checkbox')];
	allSwitches.forEach(switchElement => {
		if (switchElement.checked)
			switchElement.click();
	});

	const allSelects = [...document.querySelectorAll('.search__option select')];
	allSelects.forEach(select => {
			if (select.getAttribute('select-type') === 'min') {
				select.selectedIndex = 0;
			} else if (select.getAttribute('select-type') === 'max') {
				select.selectedIndex = select.options.length - 1;
			}
		select.dispatchEvent(new Event('change'));
	});

	const setInitialValueToRange = (element, value) => {
		element.value = value;
		const event = new KeyboardEvent("keyup", {
			key: "Enter",
			code: "Enter",
			keyCode: 13,
			which: 13,
			bubbles: false
		});

		element.dispatchEvent(event);
	};

	const allRanges = [...document.querySelectorAll('.filter-pop-up-page .search__wrapper-range'),...document.querySelectorAll('.search__wrapper-range')];
	allRanges.forEach(range => {
		const rangeElementMin = range.querySelector('.search__range-input--one')
		const initialMinValue = rangeElementMin.getAttribute('startminvalue');
		const minValueInputElement = range.querySelector('.search__option-input--min');
		setInitialValueToRange(minValueInputElement, initialMinValue);
		rangeElementMin.dispatchEvent(new Event('input'))
		const rangeElementMax = range.querySelector('.search__range-input--two')
		const initialMaxValue = rangeElementMax.getAttribute('startmaxvalue');
		const maxValueInputElement = range.querySelector('.search__option-input--max');
		setInitialValueToRange(maxValueInputElement, initialMaxValue);
		rangeElementMax.dispatchEvent(new Event('input'))
	});

	const allMultiBuildingsButtons = [...document.querySelectorAll('.active-multi-building')];
	allMultiBuildingsButtons.forEach(item => {
		item.classList.remove('active-multi-building');
	});

	MultipleSelector.uncheckAll('modelName');
	MultipleSelector.uncheckAll('multipleSearch');
	updateMultiSearch();
	updateMultiSearchState();

	selectedBuildings.clear();
}
